import { useCallback, useMemo, type ReactNode } from "react";
import type { CountBasis, SnapshotFilters, TimeframeKey } from "../data/types";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import { dashboardV2Actions } from "./dashboardV2Slice";

export interface TimeframeOption {
  key: TimeframeKey;
  label: string;
}

export const TIMEFRAME_OPTIONS: TimeframeOption[] = [
  { key: "all", label: "All" },
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "this_week", label: "This Week" },
  { key: "this_month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
  { key: "custom", label: "Custom" },
];

/** Natural-language period suffix for the selected timeframe, e.g.
    "New leads " + periodNoun(timeframe) → "New leads today" / "this month". */
const PERIOD_NOUN: Record<TimeframeKey, string> = {
  all: "all time",
  today: "today",
  yesterday: "yesterday",
  this_week: "this week",
  this_month: "this month",
  last_month: "last month",
  custom: "this range",
};
export const periodNoun = (t: TimeframeKey): string =>
  PERIOD_NOUN[t] ?? "this period";

/** "Total leads today" / "Total leads this month" — plain for custom/all ranges.
 *  The chart sums leads created over time; on "All" that total reconciles with
 *  the all-time "Total Leads" KPI, so the card is labelled "Total leads". */
export const newLeadsTitle = (t: TimeframeKey): string =>
  t === "custom" || t === "all" ? "Total leads" : `Total leads ${PERIOD_NOUN[t]}`;

interface DashboardV2FiltersValue {
  // seeding filters (drive the data)
  timeframe: TimeframeKey;
  setTimeframe: (t: TimeframeKey) => void;
  branch: string;
  setBranch: (b: string) => void;
  role: string;
  setRole: (r: string) => void;
  /** The RAIL's follow-up slot role. `undefined` = untouched → it falls back
   *  to the seeded default. See `dashboardV2Slice.followUpRole`. */
  followUpRole: string | undefined;
  setFollowUpRole: (r: string | undefined) => void;
  /** PER-WIDGET follow-up role for the two boards (2026-09-09: "they are
   *  individual widgets" — filtering one must not reload the others). */
  followUpRoleByWidget: Record<string, string | undefined>;
  setFollowUpRoleForWidget: (widget: string, r: string | undefined) => void;
  user: string;
  setUser: (u: string) => void;
  /** Country CF option id (study-abroad / industry 1); "all" = no filter. */
  country: string;
  setCountry: (c: string) => void;
  /** Selected intakes (study-abroad) as "<year>-<month0>" keys; [] = no filter. */
  intakes: string[];
  setIntakes: (keys: string[]) => void;
  /** Global CF pills: { [customInputId]: optionId }; {} = no filter. */
  globalCf: Record<string, string>;
  setGlobalCf: (fieldId: string, optionId: string | null) => void;
  customStart: string | null;
  customEnd: string | null;
  setCustomRange: (start: string | null, end: string | null) => void;
  /** count basis — leads (default) vs deals; rescales all widget counts */
  basis: CountBasis;
  setBasis: (b: CountBasis) => void;
  /** Recent Activity feed: selected type bucket ("all" = every type). */
  activityType: string;
  setActivityType: (t: string) => void;
  /** Recent Activity feed: free-text search over activity content / phone. */
  activitySearch: string;
  setActivitySearch: (q: string) => void;
  /** noun for the basis, for labels ("lead" / "deal") */
  unitNoun: string;
  /** convenience object for the data hooks */
  seedFilters: SnapshotFilters;
  // view flag (does NOT seed data). Compare is currently always false — its only
  // toggle lived in the removed detail pages — kept so widgets read a stable flag.
  compare: boolean;
  setCompare: (c: boolean) => void;
  toggleCompare: () => void;
  // reset everything to defaults
  reset: () => void;
}

/**
 * Kept as a passthrough so existing `<DashboardV2Provider>` mounts keep working.
 * The state now lives in Redux (`dashboardV2Slice`) instead of `useState` here,
 * so filters + the open detail view SURVIVE ROUTING (this component unmounting
 * no longer wipes them).
 */
export function DashboardV2Provider({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

export function useDashboardV2Filters(): DashboardV2FiltersValue {
  const dispatch = useAppDispatch();
  const s = useAppSelector((st) => st.dashboardV2);

  const setTimeframe = useCallback(
    (t: TimeframeKey) => dispatch(dashboardV2Actions.setTimeframe(t)),
    [dispatch],
  );
  const setBranch = useCallback(
    (b: string) => dispatch(dashboardV2Actions.setBranch(b)),
    [dispatch],
  );
  const setRole = useCallback(
    (r: string) => dispatch(dashboardV2Actions.setRole(r)),
    [dispatch],
  );
  const setFollowUpRole = useCallback(
    (r: string | undefined) => dispatch(dashboardV2Actions.setFollowUpRole(r)),
    [dispatch],
  );
  const setFollowUpRoleForWidget = useCallback(
    (widget: string, r: string | undefined) =>
      dispatch(dashboardV2Actions.setFollowUpRoleForWidget({ widget, role: r })),
    [dispatch],
  );
  const setUser = useCallback(
    (u: string) => dispatch(dashboardV2Actions.setUser(u)),
    [dispatch],
  );
  const setCountry = useCallback(
    (c: string) => dispatch(dashboardV2Actions.setCountry(c)),
    [dispatch],
  );
  const setIntakes = useCallback(
    (keys: string[]) => dispatch(dashboardV2Actions.setIntakes(keys)),
    [dispatch],
  );
  const setGlobalCf = useCallback(
    (fieldId: string, optionId: string | null) =>
      dispatch(dashboardV2Actions.setGlobalCf({ fieldId, optionId })),
    [dispatch],
  );
  const setCustomRange = useCallback(
    (start: string | null, end: string | null) =>
      dispatch(dashboardV2Actions.setCustomRange({ start, end })),
    [dispatch],
  );
  const setBasis = useCallback(
    (b: CountBasis) => dispatch(dashboardV2Actions.setBasis(b)),
    [dispatch],
  );
  const setCompare = useCallback(
    (c: boolean) => dispatch(dashboardV2Actions.setCompare(c)),
    [dispatch],
  );
  const toggleCompare = useCallback(
    () => dispatch(dashboardV2Actions.toggleCompare()),
    [dispatch],
  );
  const setActivityType = useCallback(
    (t: string) => dispatch(dashboardV2Actions.setActivityType(t)),
    [dispatch],
  );
  const setActivitySearch = useCallback(
    (q: string) => dispatch(dashboardV2Actions.setActivitySearch(q)),
    [dispatch],
  );
  const reset = useCallback(
    () => dispatch(dashboardV2Actions.reset()),
    [dispatch],
  );

  return useMemo<DashboardV2FiltersValue>(() => {
    const seedFilters: SnapshotFilters = {
      timeframe: s.timeframe,
      branch: s.branch,
      role: s.role,
      user: s.user,
      customStart: s.customStart,
      customEnd: s.customEnd,
      basis: s.basis,
    };
    return {
      timeframe: s.timeframe,
      setTimeframe,
      branch: s.branch,
      setBranch,
      role: s.role,
      setRole,
      followUpRole: s.followUpRole,
      setFollowUpRole,
      followUpRoleByWidget: s.followUpRoleByWidget,
      setFollowUpRoleForWidget,
      user: s.user,
      setUser,
      country: s.country,
      setCountry,
      intakes: s.intakes,
      setIntakes,
      globalCf: s.globalCf,
      setGlobalCf,
      customStart: s.customStart,
      customEnd: s.customEnd,
      setCustomRange,
      basis: s.basis,
      setBasis,
      activityType: s.activityType,
      setActivityType,
      activitySearch: s.activitySearch,
      setActivitySearch,
      unitNoun: s.basis === "deals" ? "deal" : "lead",
      seedFilters,
      compare: s.compare,
      setCompare,
      toggleCompare,
      reset,
    };
  }, [
    s,
    setTimeframe,
    setBranch,
    setRole,
    setFollowUpRole,
    setUser,
    setCountry,
    setIntakes,
    setGlobalCf,
    setCustomRange,
    setBasis,
    setActivityType,
    setActivitySearch,
    setCompare,
    toggleCompare,
    reset,
  ]);
}
